import { ExternalLink, Star, Sparkles } from "lucide-react";

const PICKS = [
  {
    id: "iso-5lb",
    title: "Isolation 5lb (Whey Isolate)",
    tagline: "24g of pure muscle-building protein per scoop",
    note:
      "If you're chasing density and a clean macro window, this is what's in my shaker post-lift. No spike, no clumps — just protein that actually moves the needle.",
    price: "$114.99",
    image:
      "https://blackstonelabs.com/cdn/shop/files/blackstone-labs-isolation-5lb-cinammon-cereal-ISO-CNC5.png?v=1751381685&width=900",
    href:
      "https://blackstonelabs.com/collections/supplements/products/5lb-isolation",
    category: "Protein",
  },
  {
    id: "eaas",
    title: "EAAs — Essential Amino Acids",
    tagline: "9 essentials, clinically proven 2:1:1 BCAA ratio",
    note:
      "Sip these intra-workout. Hydration, endurance, and the 9 aminos your body literally cannot make on its own. Non-negotiable on training days.",
    price: "$34.99",
    image:
      "https://blackstonelabs.com/cdn/shop/files/EAA-Blue-Lollipop-EAA-BLP.png?v=1738349988&width=900",
    href:
      "https://blackstonelabs.com/collections/supplements/products/eaas-essential-amino-acids",
    category: "Aminos",
  },
  {
    id: "turmeric",
    title: "Turmeric (Liposomal)",
    tagline: "Joint support · Inflammation defense",
    note:
      "Recovery is where the iron pays you back. The liposomal delivery gets it absorbed where most turmeric pills don't. My joints thank me daily.",
    price: "$44.99",
    image:
      "https://blackstonelabs.com/cdn/shop/files/blackstone-labs-turmeric-TURM_d4736711-4258-4bfc-b293-e6908ce6d22d.png?v=1735843628&width=900",
    href:
      "https://blackstonelabs.com/collections/supplements/products/turmeric",
    category: "Recovery",
  },
  {
    id: "collagen",
    title: "Instantized Collagen",
    tagline: "20g hydrolyzed collagen · skin, joints, recovery",
    note:
      "Mix it in coffee, oats, water — flavorless and dissolves instantly. Long-term game for connective tissue and the kind of look that lasts off-stage.",
    price: "$44.99",
    image:
      "https://blackstonelabs.com/cdn/shop/files/new_collagen_1200x1200_d7fadf7d-aff0-4f7e-84da-cafc90279d3d.png?v=1742923999&width=900",
    href:
      "https://blackstonelabs.com/collections/supplements/products/instantized-collagen",
    category: "Recovery",
  },
];

export default function FounderPicks() {
  return (
    <section
      id="founder-picks"
      data-testid="founder-picks-section"
      className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/3 left-0 w-[420px] h-[420px] bg-[#D4AF37] rounded-full blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 md:items-end gap-6 mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2 inline-flex items-center gap-2">
              <Sparkles size={12} /> Founder's Picks
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              In My <span className="text-[#D4AF37]">Stack.</span>
            </h2>
          </div>
          <p className="text-neutral-400 text-base md:text-lg leading-relaxed md:max-w-md md:justify-self-end">
            The exact supplements I keep on my counter — sourced from{" "}
            <a
              href="https://blackstonelabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:underline font-bold"
            >
              Blackstone Labs
            </a>
            . Real picks. Real results.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PICKS.map((p, i) => (
            <a
              key={p.id}
              data-testid={`pick-${p.id}`}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#0F0F0F] border-2 border-zinc-900 hover:border-[#D4AF37] transition-all flex flex-col"
            >
              <div className="relative aspect-square bg-white overflow-hidden border-b-2 border-zinc-900 group-hover:border-[#D4AF37] transition-colors">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 bg-black/85 text-[#D4AF37] text-[9px] uppercase tracking-widest px-2 py-1 font-bold">
                  Pick #{i + 1}
                </span>
                <span className="absolute top-3 right-3 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-2 py-1 font-bold">
                  {p.category}
                </span>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display text-xl text-white leading-tight mb-1">
                  {p.title}
                </h3>
                <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-bold mb-3">
                  {p.tagline}
                </p>
                <div className="flex items-start gap-2 mb-4">
                  <Star
                    size={14}
                    className="text-[#D4AF37] flex-shrink-0 mt-0.5"
                    fill="#D4AF37"
                  />
                  <p className="text-sm text-neutral-300 leading-relaxed italic">
                    "{p.note}"
                  </p>
                </div>
                <div className="mt-auto flex items-center justify-between pt-3 border-t border-zinc-900">
                  <span className="font-display text-2xl text-white">
                    {p.price}
                  </span>
                  <span className="text-xs text-neutral-400 group-hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition-colors">
                    Shop <ExternalLink size={12} />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <p className="text-[10px] text-neutral-600 mt-6 text-center uppercase tracking-widest">
          Affiliate-friendly · Links open at Blackstone Labs · Always read the
          label
        </p>
      </div>
    </section>
  );
}
